import React from 'react';

function Analytics({ dashboardStats }) {
  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold mb-6 text-slate-800">Analytics & Statistics</h2>
        
        {/* Main Analytics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
            <h3 className="text-lg font-bold text-blue-900 mb-2">User Growth</h3>
            <p className="text-4xl font-bold text-blue-700">{dashboardStats.totalUsers}</p>
            <p className="text-sm text-blue-600 mt-2">Total registered users</p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
            <h3 className="text-lg font-bold text-green-900 mb-2">Active Users</h3>
            <p className="text-4xl font-bold text-green-700">{dashboardStats.activeUsers}</p>
            <p className="text-sm text-green-600 mt-2">Currently active accounts</p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
            <h3 className="text-lg font-bold text-purple-900 mb-2">Premium Conversion</h3>
            <p className="text-4xl font-bold text-purple-700">
              {dashboardStats.totalUsers > 0 
                ? ((dashboardStats.premiumUsers / dashboardStats.totalUsers) * 100).toFixed(1)
                : 0}%
            </p>
            <p className="text-sm text-purple-600 mt-2">{dashboardStats.premiumUsers} premium users</p>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl">
            <h3 className="text-lg font-bold text-pink-900 mb-2">Match Success Rate</h3>
            <p className="text-4xl font-bold text-pink-700">{dashboardStats.totalMatches}</p>
            <p className="text-sm text-pink-600 mt-2">Successful matches made</p>
          </div>
        </div>

        {/* Quick Insights Section */}
        <div className="mt-8 p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl">
          <h3 className="text-lg font-bold text-yellow-900 mb-4">Quick Insights</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Free vs Premium Ratio:</span>
              <span className="font-bold text-gray-900">
                {dashboardStats.freeUsers} : {dashboardStats.premiumUsers}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Today's New Users:</span>
              <span className="font-bold text-gray-900">{dashboardStats.todayRegistrations}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Total Reports:</span>
              <span className="font-bold text-gray-900">{dashboardStats.totalReports}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Active Rate:</span>
              <span className="font-bold text-gray-900">
                {dashboardStats.totalUsers > 0 
                  ? ((dashboardStats.activeUsers / dashboardStats.totalUsers) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Detailed Statistics */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">Total Users</span>
              <span className="text-2xl">👥</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">{dashboardStats.totalUsers}</p>
            <p className="text-xs text-gray-500 mt-1">All registered accounts</p>
          </div>

          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">Active Users</span>
              <span className="text-2xl">✅</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">{dashboardStats.activeUsers}</p>
            <p className="text-xs text-gray-500 mt-1">Currently active</p>
          </div>

          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">Free Users</span>
              <span className="text-2xl">👤</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">{dashboardStats.freeUsers}</p>
            <p className="text-xs text-gray-500 mt-1">Free membership</p>
          </div>

          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">Premium Users</span>
              <span className="text-2xl">👑</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">{dashboardStats.premiumUsers}</p>
            <p className="text-xs text-gray-500 mt-1">Premium membership</p>
          </div>

          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">Total Matches</span>
              <span className="text-2xl">💕</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">{dashboardStats.totalMatches}</p>
            <p className="text-xs text-gray-500 mt-1">Successful matches</p>
          </div>

          <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-600">Reports</span>
              <span className="text-2xl">⚠️</span>
            </div>
            <p className="text-3xl font-bold text-gray-800">{dashboardStats.totalReports}</p>
            <p className="text-xs text-gray-500 mt-1">Total reports filed</p>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="mt-8 p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl">
          <h3 className="text-lg font-bold text-indigo-900 mb-4">Performance Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">User Retention Rate</p>
              <p className="text-2xl font-bold text-indigo-700">
                {dashboardStats.totalUsers > 0 
                  ? ((dashboardStats.activeUsers / dashboardStats.totalUsers) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Premium Adoption</p>
              <p className="text-2xl font-bold text-indigo-700">
                {dashboardStats.totalUsers > 0 
                  ? ((dashboardStats.premiumUsers / dashboardStats.totalUsers) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Today's Growth</p>
              <p className="text-2xl font-bold text-indigo-700">{dashboardStats.todayRegistrations}</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Match Rate</p>
              <p className="text-2xl font-bold text-indigo-700">
                {dashboardStats.activeUsers > 0 
                  ? ((dashboardStats.totalMatches / dashboardStats.activeUsers) * 100).toFixed(1)
                  : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;